/**
 * PlayerHeroRow — hero row for top scorer / featured player.
 *
 * Extracted from `IhsTopScorers.HeroRow`. Stadium-style gradient
 * background, crest (with optional country-flag overlay) on the left,
 * name + meta in the middle, big coloured value-box on the right.
 *
 * The right-hand value box is now generalised — pass any stat, not
 * just goals. Use `<ValueBox shape="rect" size="lg" labelPlace="inside">`
 * via the `stat` prop.
 *
 * Shape:
 *   { player: { name; position?; nationalityLabel?; nationalityCode? };
 *     teamBadge?: string; teamName: string;
 *     stat: { value: ReactNode; label: string; color: string } }
 */

import type { ReactElement, ReactNode } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { CountryFlag } from './CountryFlag.js';
import { TeamCrest, useTeamVisualMode } from './TeamCrest.js';
import { ValueBox } from './ValueBox.js';
import styles from './PlayerHeroRow.module.css';

const dataShape = z.object({
  player: z.object({
    name: z.string(),
    position: z.string().optional(),
    nationalityLabel: z.string().optional(),
    nationalityCode: z.string().optional(),
  }),
  teamBadge: z.string().url().optional(),
  teamName: z.string(),
  stat: z.object({
    value: z.union([z.string(), z.number()]),
    label: z.string(),
    color: z.string(),
  }),
});

export const META: PrimitiveMeta = {
  name: 'player-hero-row',
  description: 'Hero row — crest+flag, player meta, big coloured stat box.',
  composable: true,
  dataShape,
  densities: ['hero'],
  breakpoints: { mobile: true, wide: true },
  animation: 'fade-in',
  composes: ['team-crest', 'country-flag', 'value-box'],
};

interface PlayerHeroRowProps {
  player: {
    name: string;
    position?: string;
    nationalityLabel?: string;
    nationalityCode?: string;
  };
  teamBadge?: string;
  teamName: string;
  stat: {
    value: ReactNode;
    label: string;
    color: string;
  };
}

export function PlayerHeroRow({
  player,
  teamBadge,
  teamName,
  stat,
}: PlayerHeroRowProps): ReactElement {
  // ADR-063 team-visual 'none': no crest -> no puck. The wrapper slot
  // (a filled disc) would otherwise render as an empty floating dot.
  const teamVisual = useTeamVisualMode();
  // Meta line only when there is real meta — a bare "—" placeholder line
  // is noise (compact-by-default, live defect stellar 2026-07-31).
  const metaText = [player.position, player.nationalityLabel]
    .filter(Boolean)
    .join(' · ');
  // The 3-column grid template assumes the crest cell exists. In 'none'
  // mode the crest is not rendered, so without a 2-column override the
  // body auto-places into the `auto` column (sized to the full nowrap
  // name) and the ValueBox lands in the stretchy middle column — a long
  // name shoves the box off the card edge (live defect stellar
  // 2026-07-31: "Andreasen, Valdemar Bys…" clipped chip).
  const heroClass =
    teamVisual === 'none'
      ? `${styles.hero} ${styles.heroNoVisual}`
      : styles.hero;
  return (
    <div className={heroClass}>
      <div className={styles.heroOverlay} aria-hidden="true" />
      {teamVisual !== 'none' ? (
        <div className={styles.heroLeft}>
          <span className={styles.slot}>
            <TeamCrest
              {...(teamBadge !== undefined ? { src: teamBadge } : {})}
              name={teamName}
              size={56}
            />
            {player.nationalityCode ? (
              <span className={styles.flagOverlay}>
                <CountryFlag code={player.nationalityCode} size={18} />
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
      <div className={styles.heroBody}>
        <div className={styles.heroName}>{player.name}</div>
        {metaText.length > 0 ? (
          <div className={styles.heroMeta}>{metaText}</div>
        ) : null}
      </div>
      <ValueBox
        value={stat.value}
        label={stat.label}
        color={stat.color}
        shape="rect"
        size="lg"
        variant="solid"
        labelPlace="inside"
      />
    </div>
  );
}

export default PlayerHeroRow;
